import Axios from 'axios';
import { axios } from './axios';
import { handleErrors } from './utils';

export function sendMail({
    contacts = [],
    accounts = [],
    to = [],
    users = [],
    body,
    subject,
    trackingOpen,
    trackingLink,
    attachments = [],
    template,
    individual,
    opportunityId,
}) {
    return new Promise((resolve, reject) => {
        axios
            .post('/mailing/v1/send', {
                to,
                contacts,
                accounts,
                users,
                body,
                subject,
                tracking: {
                    open: !!trackingOpen,
                    link: !!trackingLink,
                },
                attachments,
                template,
                individual,
                opportunityId,
            })
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

export function createAttachment(fileName) {
    return new Promise((resolve, reject) => {
        axios
            .post('/mailing/v1/attachment', { fileName })
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

export function uploadAttachment(file) {
    return new Promise((resolve, reject) => {
        Axios.put(file.url, file)
            .then(() => {
                resolve();
            })
            .catch(handleErrors(reject));
    });
}

// Signature

export function getSignature() {
    return new Promise((resolve, reject) => {
        axios
            .get('/mailing/v1/signature')
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}

export function postSignature(body = '', enabled = true) {
    return new Promise((resolve, reject) => {
        axios
            .post('/mailing/v1/signature', {
                body,
                enabled,
            })
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}

// Templates

// =============================================================================
// Mailing Templates API (v1) - Used by Email Editor
// =============================================================================

/**
 * GET `/mailing/v1/template`. Legacy endpoint for Email Editor templates.
 * Returns templates with `template` property containing HTML content.
 *
 * @returns {Promise<unknown>}
 */
export function getTemplates() {
    return new Promise((resolve, reject) => {
        axios
            .get('/mailing/v1/template')
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}

export function createTemplate(template) {
    return new Promise((resolve, reject) => {
        axios
            .post('/mailing/v1/template', template)
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}

export function updateTemplate(id, template) {
    return new Promise((resolve, reject) => {
        axios
            .put(`/mailing/v1/template/${id}`, template)
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}

export function deleteTemplate(id) {
    return new Promise((resolve, reject) => {
        axios
            .delete(`/mailing/v1/template/${id}`)
            .then((res) => {
                resolve(res);
            })
            .catch(handleErrors(reject));
    });
}

// Variables

export function getVariables() {
    return new Promise((resolve, reject) => {
        axios
            .get(`/mailing/v1/template/variables`)
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

/**
 * @typedef {{ dailyLimit: number, monthlyLimit: number, dailyUsed: number, monthlyUsed: number }} CampaignLimits
 * @returns {Promise<CampaignLimits>}
 */
export function getCampaignLimits() {
    return new Promise((resolve, reject) => {
        axios
            .get('/mailing/v2/campaign/limits')
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

/** Campaign email (subject, preview, HTML) insert-field list — distinct from template variables. */
export function getCampaignReplaceVariables() {
    return new Promise((resolve, reject) => {
        axios
            .get('/mailing/v2/campaign/replace')
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

// Provider test email
export function sendTestEmail({ to, from, subject, previewText = '', body }) {
    return new Promise((resolve, reject) => {
        axios
            .post('/mailing/v2/provider/send', {
                to,
                from,
                subject,
                previewText,
                body,
            })
            .then(({ data }) => {
                if (data.status === 'failed') reject(data.error);
                else resolve(data);
            })
            .catch(handleErrors(reject));
    });
}

export function launchCampaign(id) {
    if (!id) return Promise.reject('Campaign id is needed');

    return new Promise((resolve, reject) => {
        axios
            .post(`/mailing/v2/campaign/${id}/launch`)
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

/**
 * @param {string|number} id
 * @param {{ sender: boolean, recipients: boolean, subject: boolean, design: boolean }} options
 */
export function duplicateCampaign(id, options) {
    if (!id) return Promise.reject('Campaign id is needed');

    return new Promise((resolve, reject) => {
        axios
            .post(`/mailing/v2/campaign/${id}/duplicate`, options)
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

/** @typedef {{ name: string, type: string }} CampaignEventType */

/**
 * @param {string|number} campaignId
 * @returns {Promise<CampaignEventType[]>}
 */
export function getCampaignEventTypes(campaignId) {
    if (campaignId == null || campaignId === '') {
        return Promise.reject('Campaign id is needed');
    }

    return new Promise((resolve, reject) => {
        axios
            .get(`/mailing/v2/campaign/${campaignId}/events/types`)
            .then(({ data }) => resolve(Array.isArray(data) ? data : []))
            .catch(handleErrors(reject));
    });
}

/**
 * @param {string|number} campaignId
 * @param {string} type
 * @returns {Promise<{ total: number }>}
 */
export function getCampaignEventsCount(campaignId, type) {
    if (campaignId == null || campaignId === '') {
        return Promise.reject('Campaign id is needed');
    }
    if (!type) {
        return Promise.reject('Event type is needed');
    }

    return new Promise((resolve, reject) => {
        axios
            .get(`/mailing/v2/campaign/${campaignId}/events/count`, { params: { type } })
            .then(({ data }) => resolve(data && typeof data === 'object' ? data : { total: 0 }))
            .catch(handleErrors(reject));
    });
}

/**
 * @param {string|number} campaignId
 * @param {{ type: string, offset?: number, count?: number }} query
 * @returns {Promise<Record<string, unknown>[]>}
 */
export function getCampaignEvents(campaignId, { type, offset = 0, count = 10 } = {}) {
    if (campaignId == null || campaignId === '') {
        return Promise.reject('Campaign id is needed');
    }
    if (!type) {
        return Promise.reject('Event type is needed');
    }

    return new Promise((resolve, reject) => {
        axios
            .get(`/mailing/v2/campaign/${campaignId}/events`, {
                params: { type, offset, count },
            })
            .then(({ data }) => resolve(Array.isArray(data) ? data : []))
            .catch(handleErrors(reject));
    });
}

/**
 * @param {string|number} campaignId
 * @returns {Promise<{campaign: object, engagementScorecards: object, engagementFunnel: object, deliveryHealth: object, linkPerformance: array}>}
 */
export function getCampaignEventsDetail(campaignId) {
    if (campaignId == null || campaignId === '') {
        return Promise.reject('Campaign id is needed');
    }

    return new Promise((resolve, reject) => {
        axios
            .get(`/mailing/v2/campaign/${campaignId}/events/detail`)
            .then(({ data }) => resolve(data && typeof data === 'object' ? data : {}))
            .catch(handleErrors(reject));
    });
}

/**
 * @typedef {{ granularity: string, points: Array<{timestamp: string, uniqueOpens: number, uniqueClicks: number}> }} PerformanceOverTime
 * @param {string|number} campaignId
 * @param {{ preset?: string, from?: string, to?: string }} query
 * @returns {Promise<{performanceOverTime: PerformanceOverTime}>}
 */
export function getCampaignEventsAnalytics(campaignId, { preset, from, to } = {}) {
    if (campaignId == null || campaignId === '') {
        return Promise.reject('Campaign id is needed');
    }

    const params = {};
    if (from != null && from !== '' && to != null && to !== '') {
        params.from = from;
        params.to = to;
    } else if (preset != null && preset !== '') {
        params.preset = preset;
    }

    return new Promise((resolve, reject) => {
        axios
            .get(`/mailing/v2/campaign/${campaignId}/events/analytics`, {
                params,
            })
            .then(({ data }) => resolve(data && typeof data === 'object' ? data : {}))
            .catch(handleErrors(reject));
    });
}
